import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SEO from '../../components/SEO';
import FAQ from '../../components/ui/FAQ';
import { reinforcementLearningFAQs } from '../../data/faqs';
import { sendRequirementsFormEmail } from '../../utils/emailService';
import { scrollToElement } from '../../utils/scrollUtils';
import { useContactInfo } from '../../utils/contactObfuscation';

const ReinforcementLearningLanding = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: 'Reinforcement Learning',
    requirements: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const contactInfo = useContactInfo();

  const seoData = {
    title: 'Reinforcement Learning Services - RL Models & Agents | OctalChip',
    description: 'Professional reinforcement learning development services. We build intelligent RL agents and models for autonomous decision-making, game AI, robotics, and optimization problems.',
    keywords: 'reinforcement learning, RL development, RL agents, Q-learning, deep RL, policy gradient, autonomous systems, game AI, robotics',
    canonical: 'https://www.octalchip.com/landing/reinforcement-learning',
    faqData: reinforcementLearningFAQs,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Reinforcement Learning Development Services",
      "description": "Intelligent RL agents and models for autonomous decision-making and optimization",
      "provider": {
        "@type": "Organization",
        "name": "OctalChip",
        "url": "https://www.octalchip.com"
      }
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const result = await sendRequirementsFormEmail(formData);
      
      if (result.success) {
        setSubmitMessage('Thank you! Your requirements have been submitted successfully. We\'ll get back to you within 24 hours with a detailed quote.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          projectType: 'Reinforcement Learning',
          requirements: ''
        });
      } else {
        setSubmitMessage('Sorry, there was an error submitting your requirements. Please try again or contact us directly.');
      }
    } catch (error) {
      setSubmitMessage('Sorry, there was an error submitting your requirements. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: 'Brain',
      title: 'RL Agent Development',
      description: 'Custom agents that learn optimal policies through interaction'
    },
    {
      icon: 'Network',
      title: 'Deep Q-Networks',
      description: 'Advanced DQN for complex decision-making'
    },
    {
      icon: 'TrendingUp',
      title: 'Policy Gradient Methods',
      description: 'Policy optimization using REINFORCE and PPO'
    },
    {
      icon: 'Users',
      title: 'Multi-Agent Systems',
      description: 'Collaborative and competitive multi-agent RL'
    }
  ];

  const technologies = [
    { name: 'PyTorch', description: 'Deep learning framework' },
    { name: 'TensorFlow', description: 'Google\'s RL framework' },
    { name: 'OpenAI Gym', description: 'RL environments' },
    { name: 'Stable Baselines3', description: 'RL algorithms' },
    { name: 'Ray RLlib', description: 'Scalable RL library' },
    { name: 'Unity ML-Agents', description: 'Unity-based RL' }
  ];

  const useCases = [
    {
      title: 'Game AI & Strategy',
      description: 'Develop intelligent game-playing agents for chess, Go, and video games',
      icon: 'Target'
    },
    {
      title: 'Robotics',
      description: 'Train robots to perform complex tasks through reinforcement learning',
      icon: 'Settings'
    },
    {
      title: 'Autonomous Systems',
      description: 'Build self-driving cars and autonomous navigation systems',
      icon: 'Car'
    },
    {
      title: 'Optimization Problems',
      description: 'Solve complex optimization challenges with RL agents',
      icon: 'Zap'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={seoData.canonical}
        faqData={seoData.faqData}
        structuredData={seoData.structuredData}
      />
      <Header />
      
      {/* Hero Section */}
      <section id="hero" className="py-12 sm:py-16 md:py-20 lg:py-24 bg-gradient-to-br from-purple-50 via-white to-indigo-50">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div className="text-center lg:text-left">
                <div className="inline-block bg-[#00d690]/10 text-[#00d690] px-4 py-2 rounded-full text-sm font-medium mb-6">
                  Reinforcement Learning
                </div>
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight text-gray-800">
                  World's Best Reinforcement Learning Services - Build Intelligent Agents Globally
                </h1>
                <p className="text-lg md:text-xl mb-8 text-gray-600 leading-relaxed">
                  Trusted by businesses worldwide, we create reinforcement learning agents and models for autonomous decision-making, game AI, robotics, and optimization. Get RL solutions that learn from experience and improve over time - recognized globally for excellence.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
                  <Button
                    variant="primary"
                    animation="pulse"
                    className="px-8 py-4 text-base md:text-lg bg-[#00d690] text-white hover:bg-[#00b876] shadow-lg"
                    onClick={async () => {
                      await scrollToElement('#get-quote', { 
                        smooth: true,
                        maxRetries: 15,
                        retryDelay: 150
                      }).catch(() => {});
                    }}
                  >
                    Get Free Quote
                  </Button>
                  <Button
                    variant="outline"
                    animation="glow"
                    className="px-8 py-4 text-base md:text-lg border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white"
                    onClick={() => navigate('/services/ai-ml-services/reinforcement-learning')}
                  >
                    View Full Service
                  </Button>
                </div>

                <div className="flex flex-wrap gap-6 text-sm justify-center lg:justify-start">
                  <div className="flex items-center">
                    <Icon name="Check" size={20} className="text-[#00d690] mr-2" />
                    <span className="text-gray-700">RL Agents</span>
                  </div>
                  <div className="flex items-center">
                    <Icon name="Check" size={20} className="text-[#00d690] mr-2" />
                    <span className="text-gray-700">Game AI</span>
                  </div>
                  <div className="flex items-center">
                    <Icon name="Check" size={20} className="text-[#00d690] mr-2" />
                    <span className="text-gray-700">Robotics</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-xl border border-gray-100">
                <div className="text-center">
                  <div className="w-16 h-16 bg-[#00d690]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="MessageSquare" size={32} className="text-[#00d690]" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-gray-800">Get Your Free Quote</h3>
                  <p className="text-gray-600 mb-6">Tell us about your RL project and get a detailed estimate in 24 hours</p>
                  
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <input 
                      type="text" 
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                      placeholder="Your Name"
                      required
                    />
                    <input 
                      type="email" 
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                      placeholder="Your Email"
                      required
                    />
                    <input 
                      type="tel" 
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                      placeholder="Your Phone"
                      required
                    />
                    <textarea 
                      name="requirements"
                      value={formData.requirements}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                      placeholder="Describe your RL project..."
                      required
                    ></textarea>
                    
                    {submitMessage && (
                      <div className={`p-4 rounded-lg text-center ${
                        submitMessage.includes('Thank you') 
                          ? 'bg-green-100 text-green-700 border border-green-200' 
                          : 'bg-[#00d690]/10 text-red-700 border border-red-200'
                      }`}>
                        {submitMessage}
                      </div>
                    )}
                    
                    <Button 
                      type="submit"
                      variant="primary"
                      animation="wiggle"
                      className="w-full py-3 bg-[#00d690] text-white hover:bg-[#00b876] shadow-lg"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Submitting...' : 'Get Free Quote'}
                    </Button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            <div className="text-center">
              <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#00d690] mb-2">8+</div>
              <div className="text-sm md:text-base text-gray-600">RL Projects</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#00d690] mb-2">93%</div>
              <div className="text-sm md:text-base text-gray-600">Success Rate</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#00d690] mb-2">8-16</div>
              <div className="text-sm md:text-base text-gray-600">Weeks Timeline</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#00d690] mb-2">24/7</div>
              <div className="text-sm md:text-base text-gray-600">Support Available</div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">Why Choose Our Reinforcement Learning Services?</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              We deliver RL agents that learn from experience and improve over time
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center p-6 bg-gray-50 rounded-xl hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-[#00d690]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name={benefit.icon} size={32} className="text-[#00d690]" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section id="technologies" className="py-12 sm:py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">Our RL Technology Stack</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              We use the latest reinforcement learning frameworks and platforms
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {technologies.map((tech, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow text-center">
                <h3 className="font-bold text-gray-800 mb-2">{tech.name}</h3>
                <p className="text-sm text-gray-600">{tech.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section id="use-cases" className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">What We Build</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              From game AI to robotics, we build reinforcement learning solutions for every need
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {useCases.map((useCase, index) => (
              <div key={index} className="bg-gray-50 p-6 md:p-8 rounded-xl hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon name={useCase.icon} size={24} className="text-[#00d690]" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">{useCase.title}</h3>
                <p className="text-gray-600">{useCase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section id="pain-points" className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">Common Reinforcement Learning Challenges We Solve</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              We understand the pain points businesses face. Here's how we help solve them
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto">
            <div className="bg-green-50 border-l-4 border-[#00d690] p-6 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertCircle" size={24} className="text-[#00d690]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Slow Learning Convergence</h3>
                  <p className="text-gray-600">RL agents take too long to learn optimal policies. We optimize algorithms, reward shaping, and hyperparameters reducing training time by 60-70% while improving performance.</p>
                </div>
              </div>
            </div>
            <div className="bg-green-50 border-l-4 border-[#00d690] p-6 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertCircle" size={24} className="text-[#00d690]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Reward Design Complexity</h3>
                  <p className="text-gray-600">Poor reward functions lead to suboptimal behavior. We design effective reward structures and use reward shaping techniques ensuring agents learn desired behaviors.</p>
                </div>
              </div>
            </div>
            <div className="bg-green-50 border-l-4 border-[#00d690] p-6 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertCircle" size={24} className="text-[#00d690]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Exploration vs Exploitation</h3>
                  <p className="text-gray-600">Balancing exploration and exploitation is challenging. We implement advanced exploration strategies and multi-armed bandit algorithms optimizing this trade-off for better results.</p>
                </div>
              </div>
            </div>
            <div className="bg-green-50 border-l-4 border-[#00d690] p-6 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertCircle" size={24} className="text-[#00d690]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">High Sample Complexity</h3>
                  <p className="text-gray-600">RL requires millions of interactions to learn. We use transfer learning, simulation environments, and efficient algorithms reducing sample requirements by 50-70%.</p>
                </div>
              </div>
            </div>
            <div className="bg-green-50 border-l-4 border-[#00d690] p-6 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertCircle" size={24} className="text-[#00d690]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Stability Issues</h3>
                  <p className="text-gray-600">RL training is unstable and unpredictable. We implement stable algorithms, proper initialization, and regularization techniques ensuring consistent and reliable training.</p>
                </div>
              </div>
            </div>
            <div className="bg-green-50 border-l-4 border-[#00d690] p-6 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertCircle" size={24} className="text-[#00d690]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Real-World Deployment Challenges</h3>
                  <p className="text-gray-600">RL agents trained in simulation often fail in real environments. We use domain randomization, sim-to-real transfer, and robust training ensuring agents work reliably in production.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="py-12 sm:py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">Our RL Development Process</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              A proven methodology that ensures quality, transparency, and on-time delivery
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            <div className="bg-white p-6 md:p-8 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-[#00d690] text-white rounded-full flex items-center justify-center font-bold text-lg mb-4">
                01
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Environment Setup</h3>
              <p className="text-gray-600">Design and set up the RL environment</p>
            </div>
            <div className="bg-white p-6 md:p-8 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-[#00d690] text-white rounded-full flex items-center justify-center font-bold text-lg mb-4">
                02
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Agent Design</h3>
              <p className="text-gray-600">Design RL agent and reward structure</p>
            </div>
            <div className="bg-white p-6 md:p-8 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-[#00d690] text-white rounded-full flex items-center justify-center font-bold text-lg mb-4">
                03
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Training & Learning</h3>
              <p className="text-gray-600">Train agent and optimize learning</p>
            </div>
            <div className="bg-white p-6 md:p-8 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-[#00d690] text-white rounded-full flex items-center justify-center font-bold text-lg mb-4">
                04
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Deployment & Testing</h3>
              <p className="text-gray-600">Deploy RL agent and test performance</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">Client Success Stories</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-5xl mx-auto">
            <div className="bg-gray-50 p-6 md:p-8 rounded-xl">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Icon key={i} name="Star" size={20} className="text-yellow-400 mr-1" />
                ))}
              </div>
              <p className="text-gray-600 mb-4 italic leading-relaxed">"OctalChip built an RL agent for our game AI. The agent learned to play at expert level within weeks. Their expertise in reinforcement learning is exceptional."</p>
              <div>
                <p className="font-semibold text-gray-800">Alex Thompson</p>
                <p className="text-sm text-gray-500">GameTech Studios</p>
              </div>
            </div>
            <div className="bg-gray-50 p-6 md:p-8 rounded-xl">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Icon key={i} name="Star" size={20} className="text-yellow-400 mr-1" />
                ))}
              </div>
              <p className="text-gray-600 mb-4 italic leading-relaxed">"The RL solution they developed for our robotics system improved performance by 40% over time. The agent continuously learns and adapts to new scenarios."</p>
              <div>
                <p className="font-semibold text-gray-800">David Martinez</p>
                <p className="text-sm text-gray-500">InnovateTech Solutions</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">Our Reinforcement Learning Portfolio</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              See how we've helped businesses worldwide build successful RL solutions
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="bg-gray-50 p-6 md:p-8 rounded-xl hover:shadow-lg transition-shadow border border-gray-200">
              <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-4">
                <Icon name="Network" size={32} className="text-[#00d690]" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Autonomous Decision System</h3>
              <p className="text-gray-600 mb-4">Built RL agent for autonomous resource allocation optimizing operations in real-time. Improved efficiency by 45% and reduced costs by 30% through intelligent decision-making.</p>
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">DQN</span>
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">PyTorch</span>
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">Real-time</span>
              </div>
              <p className="text-sm text-gray-500">Client: OperationsTech Inc | Location: USA</p>
            </div>
            <div className="bg-gray-50 p-6 md:p-8 rounded-xl hover:shadow-lg transition-shadow border border-gray-200">
              <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-4">
                <Icon name="Zap" size={32} className="text-[#00d690]" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Game AI Agent</h3>
              <p className="text-gray-600 mb-4">Developed RL agent for game AI achieving superhuman performance. Agent learned optimal strategies through self-play improving game experience and player engagement.</p>
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">PPO</span>
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">Self-play</span>
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">Game AI</span>
              </div>
              <p className="text-sm text-gray-500">Client: GameStudio Solutions | Location: UK</p>
            </div>
            <div className="bg-gray-50 p-6 md:p-8 rounded-xl hover:shadow-lg transition-shadow border border-gray-200">
              <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-4">
                <Icon name="Target" size={32} className="text-[#00d690]" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Optimization Agent</h3>
              <p className="text-gray-600 mb-4">Created RL-based optimization system for logistics and supply chain. Optimized routing and scheduling reducing delivery times by 35% and costs by 25%.</p>
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">A3C</span>
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">Optimization</span>
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">Logistics</span>
              </div>
              <p className="text-sm text-gray-500">Client: LogisticsCorp | Location: Canada</p>
            </div>
          </div>
        </div>
      </section>

      {/* Special Offers Section */}
      <section id="offers" className="py-12 sm:py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">Special Offers & Free Trials</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              Start your RL project with confidence - try our services risk-free
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-200 relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <span className="bg-[#00d690] text-white px-3 py-1 rounded-full text-sm font-medium">POPULAR</span>
              </div>
              <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-6">
                <Icon name="Play" size={32} className="text-[#00d690]" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-800">Free Trial</h3>
              <p className="text-gray-600 mb-6">Get 2 hours of free RL development work to experience our quality</p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>2 hours of free development</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>No commitment required</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>Keep the agents you receive</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>Full project consultation</span></li>
              </ul>
              <Button variant="primary" animation="shake" className="w-full bg-[#00d690] text-white hover:bg-[#00b876]" onClick={async () => { await scrollToElement('#get-quote', { smooth: true, maxRetries: 15, retryDelay: 150 }).catch(() => {}); }}>Start Free Trial</Button>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-200 relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <span className="bg-[#00d690] text-white px-3 py-1 rounded-full text-sm font-medium">NEW</span>
              </div>
              <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-6">
                <Icon name="Gift" size={32} className="text-[#00d690]" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-800">Free Credits</h3>
              <p className="text-gray-600 mb-6">Get $500 in RL development credits when you sign up</p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>$500 free credits</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>Valid for 30 days</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>Deducted from final invoice</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>Use on any project</span></li>
              </ul>
              <Button variant="primary" animation="float" className="w-full bg-[#00d690] text-white hover:bg-[#00b876]" onClick={async () => { await scrollToElement('#get-quote', { smooth: true, maxRetries: 15, retryDelay: 150 }).catch(() => {}); }}>Claim Free Credits</Button>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-200 relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <span className="bg-[#00d690] text-white px-3 py-1 rounded-full text-sm font-medium">GUARANTEE</span>
              </div>
              <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-6">
                <Icon name="Shield" size={32} className="text-[#00d690]" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-800">100% Satisfaction</h3>
              <p className="text-gray-600 mb-6">Not satisfied? Get your money back within 7 days</p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>7-day money-back guarantee</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>No questions asked</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>Keep all deliverables</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>Full refund guarantee</span></li>
              </ul>
              <Button variant="primary" animation="wiggle" className="w-full bg-[#00d690] text-white hover:bg-[#00b876]" onClick={async () => { await scrollToElement('#get-quote', { smooth: true, maxRetries: 15, retryDelay: 150 }).catch(() => {}); }}>Start Risk-Free</Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators Section */}
      <section id="trust" className="py-12 bg-white border-t border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="flex flex-wrap justify-center items-center gap-8 sm:gap-12">
            <div className="flex items-center gap-3">
              <Icon name="Shield" size={24} className="text-[#00d690]" />
              <span className="text-sm sm:text-base font-medium text-gray-700">ISO Certified</span>
            </div>
            <div className="flex items-center gap-3">
              <Icon name="Lock" size={24} className="text-[#00d690]" />
              <span className="text-sm sm:text-base font-medium text-gray-700">GDPR Compliant</span>
            </div>
            <div className="flex items-center gap-3">
              <Icon name="FileText" size={24} className="text-[#00d690]" />
              <span className="text-sm sm:text-base font-medium text-gray-700">NDA Protected</span>
            </div>
            <div className="flex items-center gap-3">
              <Icon name="CheckCircle" size={24} className="text-[#00d690]" />
              <span className="text-sm sm:text-base font-medium text-gray-700">Money-Back Guarantee</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="get-quote" className="py-12 sm:py-16 md:py-20 bg-gradient-to-br from-[#f4faf8] via-white to-[#e8f5e8]">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-block bg-[#00d690]/10 text-[#00d690] px-4 py-2 rounded-full text-sm font-medium mb-6">
              Start Your RL Project Today
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 text-gray-800">Ready to Build Intelligent RL Agents?</h2>
            <p className="text-lg md:text-xl mb-8 text-gray-600">
              Get a free consultation and detailed quote for your reinforcement learning project.
            </p>
            
            <div className="bg-white rounded-2xl p-6 md:p-8 max-w-2xl mx-auto shadow-xl border border-gray-100">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input 
                    type="text" 
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                    placeholder="Your Name"
                    required
                  />
                  <input 
                    type="email" 
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                    placeholder="Your Email"
                    required
                  />
                </div>
                <input 
                  type="tel" 
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                  placeholder="Your Phone"
                  required
                />
                <textarea 
                  name="requirements"
                  value={formData.requirements}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                  placeholder="Describe your reinforcement learning requirements..."
                  required
                ></textarea>
                
                {submitMessage && (
                  <div className={`p-4 rounded-lg text-center ${
                    submitMessage.includes('Thank you') 
                      ? 'bg-green-100 text-green-700 border border-green-200' 
                      : 'bg-[#00d690]/10 text-red-700 border border-red-200'
                  }`}>
                    {submitMessage}
                  </div>
                )}
                
                <Button 
                  type="submit"
                  variant="primary"
                  animation="glow"
                  className="w-full py-3 bg-[#00d690] text-white hover:bg-[#00b876] shadow-lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Get Free Quote & Consultation'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ 
        faqs={reinforcementLearningFAQs}
        title="Frequently Asked Questions"
        description="Common questions about our reinforcement learning services."
      />

      <Footer />
    </div>
  );
};

export default ReinforcementLearningLanding;

